import { get, isNil } from 'lodash'
import deepCamelizeKeys from '../utils/object/deepCamelizeKeys'
import {
  WEBSOCKET_URL,
  ACTION_CABLE_COMMANDS,
  ACTION_CABLE_TYPES,
  RECONNECT_DELAY_MS,
  MAX_RECONNECT_ATTEMPTS,
} from './constants'

const createActionCableClient = (options = {}) => {
  const {
    getToken,
    onMessage,
    onConnect,
    onDisconnect,
    onError,
    onSubscriptionConfirmed,
    onSubscriptionRejected,
  } = options

  let socket = null
  let reconnectAttempts = 0
  let reconnectTimer = null
  let isConnecting = false
  const subscriptions = new Map()

  const clearTimers = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  const buildIdentifier = (channel, params = {}) => {
    return JSON.stringify({ channel, ...params })
  }

  const sendCommand = (command, identifier, data = {}) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.warn('ActionCable: Socket not ready')
      return false
    }

    const message = {
      command,
      identifier,
      ...data,
    }

    socket.send(JSON.stringify(message))
    return true
  }

  const resubscribeAll = () => {
    subscriptions.forEach((params, identifier) => {
      sendCommand(ACTION_CABLE_COMMANDS.SUBSCRIBE, identifier)
    })
  }

  const handleMessage = (data) => {
    let message
    try {
      message = JSON.parse(data)
    } catch (error) {
      console.error('ActionCable: Failed to parse message', error)
      return
    }

    const type = get(message, 'type')
    const identifier = get(message, 'identifier')

    switch (type) {
      case ACTION_CABLE_TYPES.WELCOME:
        // Connection established, resubscribe to all channels
        resubscribeAll()
        break

      case ACTION_CABLE_TYPES.PING:
        // Server keep-alive, no action needed
        break

      case ACTION_CABLE_TYPES.CONFIRM_SUBSCRIPTION:
        if (onSubscriptionConfirmed) {
          onSubscriptionConfirmed(identifier)
        }
        break

      case ACTION_CABLE_TYPES.REJECT_SUBSCRIPTION:
        if (onSubscriptionRejected) {
          onSubscriptionRejected(identifier)
        }
        break

      default:
        // Regular message with data
        if (message.message && onMessage) {
          const camelizedMessage = deepCamelizeKeys(message.message)
          onMessage(identifier, camelizedMessage)
        }
        break
    }
  }

  const scheduleReconnect = () => {
    reconnectAttempts++
    const delay = RECONNECT_DELAY_MS * Math.min(reconnectAttempts, 5)

    reconnectTimer = setTimeout(() => {
      connect().catch((error) => {
        console.error('ActionCable: Reconnect failed', error)
      })
    }, delay)
  }

  const connect = () => {
    // Prevent concurrent connection attempts
    if (isConnecting) {
      return Promise.reject(new Error('Connection already in progress'))
    }

    // If already connected, just resolve
    if (socket && socket.readyState === WebSocket.OPEN) {
      return Promise.resolve()
    }

    // Close existing socket before creating new one
    if (socket) {
      socket.onclose = null
      socket.onerror = null
      socket.onmessage = null
      socket.close(1000, 'Reconnecting')
      socket = null
    }

    isConnecting = true

    return getToken()
      .then((token) => {
        if (isNil(token)) {
          isConnecting = false
          return Promise.reject(new Error('No token available'))
        }

        const url = `${WEBSOCKET_URL}?token=${token}`

        return new Promise((resolve, reject) => {
          socket = new WebSocket(url)

          socket.onopen = () => {
            isConnecting = false
            reconnectAttempts = 0
            if (onConnect) onConnect()
            resolve()
          }

          socket.onclose = (event) => {
            isConnecting = false
            clearTimers()
            if (onDisconnect) onDisconnect(event)

            // Auto-reconnect if not a clean close
            if (!event.wasClean && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
              scheduleReconnect()
            }
          }

          socket.onerror = (error) => {
            isConnecting = false
            if (onError) onError(error)
            reject(error)
          }

          socket.onmessage = (event) => {
            handleMessage(event.data)
          }
        })
      })
      .catch((error) => {
        isConnecting = false
        throw error
      })
  }

  const subscribe = (channel, params = {}) => {
    const identifier = buildIdentifier(channel, params)
    subscriptions.set(identifier, { channel, ...params })

    if (socket && socket.readyState === WebSocket.OPEN) {
      sendCommand(ACTION_CABLE_COMMANDS.SUBSCRIBE, identifier)
    }

    return identifier
  }

  const unsubscribe = (identifier) => {
    subscriptions.delete(identifier)

    if (socket && socket.readyState === WebSocket.OPEN) {
      sendCommand(ACTION_CABLE_COMMANDS.UNSUBSCRIBE, identifier)
    }
  }

  const disconnect = () => {
    clearTimers()
    subscriptions.clear()
    isConnecting = false

    if (socket) {
      socket.onclose = null
      socket.onerror = null
      socket.onmessage = null
      socket.close(1000, 'Client disconnect')
      socket = null
    }
  }

  const isConnected = () => {
    return socket && socket.readyState === WebSocket.OPEN
  }

  return {
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    isConnected,
  }
}

export default createActionCableClient
